/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useContext, useMemo, useCallback, useRef } from 'react';
import Map, { Marker, NavigationControl, Source, Layer } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import userPermissionContext from '../../../context/userPermissionContext';
import { getOsmRasterStyle } from '../../map/osmRasterStyle';
import { enqueueGeocode } from '../../map/nominatimGeocode';
import { maplibreLib } from '../../map/maplibreLoader';

function vehicleKey(m, index) {
    return String(m.name || m.device_name || m.device_id || m.id || `v-${index}`);
}

function getMarkerCoords(m) {
    if (!m) return null;
    const lat = Number(m.latitude ?? m.lat);
    const lng = Number(m.longitude ?? m.lng ?? m.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
}

function haversineKm(lat1, lng1, lat2, lng2) {
    const toRad = (d) => (d * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

/** Bearing from point a → b, degrees clockwise from north [0, 360). */
function bearingDeg(lat1, lng1, lat2, lng2) {
    const toRad = (d) => (d * Math.PI) / 180;
    const toDeg = (r) => (r * 180) / Math.PI;
    const y = Math.sin(toRad(lng2 - lng1)) * Math.cos(toRad(lat2));
    const x =
        Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
        Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(toRad(lng2 - lng1));
    return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

/** Smallest angle between two compass bearings, degrees [0, 180]. */
function angleDiffDeg(a, b) {
    const d = Math.abs(a - b) % 360;
    return d > 180 ? 360 - d : d;
}

/** Segments count only if turn from previous segment is ≤ this (same-direction travel). */
const MAX_SAME_DIRECTION_TURN_DEG = 105;

function segmentKm(p0, p1) {
    return haversineKm(p0.lat, p0.lng, p1.lat, p1.lng);
}

function bearingSegment(p0, p1) {
    return bearingDeg(p0.lat, p0.lng, p1.lat, p1.lng);
}

/** Sum of segment lengths where each step continues in roughly the same heading (no sharp zigzag / reverse). */
function sameDirectionTrailKm(points, maxTurnDeg = MAX_SAME_DIRECTION_TURN_DEG) {
    if (!points || points.length < 2) return 0;
    let sum = segmentKm(points[0], points[1]);
    let prevB = bearingSegment(points[0], points[1]);
    for (let i = 2; i < points.length; i++) {
        const pA = points[i - 1];
        const pB = points[i];
        const b = bearingSegment(pA, pB);
        if (angleDiffDeg(prevB, b) <= maxTurnDeg) {
            sum += segmentKm(pA, pB);
        }
        prevB = b;
    }
    return sum;
}

/**
 * Split trail into polylines that only follow same-direction segments (for map draw).
 * Sharp turns start a new line so the map shows motion in the current direction of travel.
 */
function sameDirectionLineStrings(points, maxTurnDeg = MAX_SAME_DIRECTION_TURN_DEG) {
    if (!points || points.length < 2) return [];
    const lines = [];
    let coords = [
        [points[0].lng, points[0].lat],
        [points[1].lng, points[1].lat],
    ];
    let prevB = bearingSegment(points[0], points[1]);
    for (let i = 2; i < points.length; i++) {
        const b = bearingSegment(points[i - 1], points[i]);
        if (angleDiffDeg(prevB, b) <= maxTurnDeg) {
            coords.push([points[i].lng, points[i].lat]);
        } else {
            if (coords.length >= 2) {
                lines.push(coords);
            }
            coords = [
                [points[i - 1].lng, points[i - 1].lat],
                [points[i].lng, points[i].lat],
            ];
        }
        prevB = b;
    }
    if (coords.length >= 2) {
        lines.push(coords);
    }
    return lines;
}

function colorForKey(k) {
    let h = 0;
    for (let i = 0; i < k.length; i++) {
        h = k.charCodeAt(i) + ((h << 5) - h);
    }
    const hue = Math.abs(h) % 360;
    return `hsl(${hue}, 72%, 42%)`;
}

const TRAIL_LINE_LAYER_ID = 'live-trails-line';

/** Below this zoom, show total vehicle count only; at or above, show each marker. */
const MIN_ZOOM_VEHICLE_MARKERS = 11;
// Reference icon points north by default.
const CAR_ICON_HEADING_OFFSET_DEG = 0;


function UberLikeCarMarker({ rotationDeg, moving }) {
    const bodyColor = moving ? '#111111' : '#6c757d';
    return (
        <div
            style={{
                width: 44,
                height: 44,
                transform: `rotate(${rotationDeg}deg)`,
                transformOrigin: '50% 50%',
                filter: 'none',
            }}
        >
            <svg width="44" height="44" viewBox="-22 -22 44 44" style={{ display: 'block' }}>
                <ellipse cx="0" cy="2" rx="9" ry="14" fill="rgba(0,0,0,0.18)" />
                <rect x="-7.5" y="-13.5" width="15" height="27" rx="3.5" ry="3.5" fill={bodyColor} stroke="#ffffff" strokeWidth="1.2" />
                <path d="M -5.5 -10 L 5.5 -10 L 4.5 -4 L -4.5 -4 Z" fill="#bfe0ff" opacity="0.95" />
                <path d="M -4.5 5 L 4.5 5 L 5.5 10 L -5.5 10 Z" fill="#bfe0ff" opacity="0.6" />
                <rect x="-6.2" y="-13" width="2.4" height="1.8" rx="0.6" fill="#fff8c2" />
                <rect x="3.8" y="-13" width="2.4" height="1.8" rx="0.6" fill="#fff8c2" />
                <rect x="-6.2" y="11.2" width="2.4" height="1.6" rx="0.5" fill="#ff5b5b" />
                <rect x="3.8" y="11.2" width="2.4" height="1.6" rx="0.5" fill="#ff5b5b" />
            </svg>
        </div>
    );
}

function headingForTrail(points) {
    if (!points || points.length < 2) return 0;
    const a = points[points.length - 2];
    const b = points[points.length - 1];
    return bearingDeg(a.lat, a.lng, b.lat, b.lng);
}

function VehicleLiveLocation({ deviceNameFilter = '' }) {
    const { jwt } = useContext(userPermissionContext);
    const normalizedDeviceFilter = String(deviceNameFilter || '').trim();
    const hasDeviceFilter = normalizedDeviceFilter.length > 0;
    const filterToDevice = useCallback(
        (rows) => {
            if (!Array.isArray(rows)) return [];
            if (!hasDeviceFilter) return rows;
            const needle = normalizedDeviceFilter.toLowerCase();
            return rows.filter((item) => {
                const dn = String(item?.device_name || '').trim().toLowerCase();
                const nm = String(item?.name || '').trim().toLowerCase();
                return dn === needle || nm === needle;
            });
        },
        [hasDeviceFilter, normalizedDeviceFilter]
    );
    const [liveLocationData, setLiveLocationData] = useState([]);
    const [trails, setTrails] = useState({});
    const [snappedTrails, setSnappedTrails] = useState({});
    const [selectedKey, setSelectedKey] = useState(null);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [locationName, setLocationName] = useState('');
    const [trailAnimNow, setTrailAnimNow] = useState(0);
    const segmentAnimRef = useRef({});
    const SEGMENT_ANIM_DURATION_MS = 1200;
    const lastTrailsLenRef = useRef({});

    const mapRef = useRef(null);

    const mapStyle = useMemo(() => getOsmRasterStyle(), []);

    const mapDivStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);

    const initialViewState = useMemo(
        () => ({
            longitude: 90.3444,
            latitude: 23.6943,
            zoom: hasDeviceFilter ? 14 : 8,
            bearing: 0,
            pitch: 0,
            padding: { top: 0, bottom: 0, left: 0, right: 0 },
        }),
        [hasDeviceFilter]
    );

    const [mapZoom, setMapZoom] = useState(() => initialViewState.zoom);

    useEffect(() => {
        const token = jwt?.token;
        const trackerToken = localStorage.getItem('TrackerToken');
        const apiBase =
            typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL
                ? process.env.REACT_APP_API_URL
                : '';
        if (!token || !trackerToken || !apiBase) {
            getLiveLocation();
            const interval = setInterval(() => {
                getLiveLocation();
            }, 10000);
            return () => clearInterval(interval);
        }

        getLiveLocation();

        const url =
            `${apiBase}liveTracStream` +
            `?token=${encodeURIComponent(token)}` +
            `&tracker_token=${encodeURIComponent(trackerToken)}` +
            (hasDeviceFilter ? `&device_name=${encodeURIComponent(normalizedDeviceFilter)}` : '');

        let es = null;
        let fallbackInterval = null;
        let consecutiveErrors = 0;

        const startFallbackPolling = () => {
            if (fallbackInterval) return;
            fallbackInterval = setInterval(() => getLiveLocation(), 10000);
        };

        const applyLiveData = (next) => {
            const nextRows = filterToDevice(next);
            if (!Array.isArray(nextRows)) return;
            setLiveLocationData((prev) => {
                if (Array.isArray(prev) && prev.length === nextRows.length) {
                    let same = true;
                    for (let i = 0; i < nextRows.length; i++) {
                        const a = prev[i];
                        const b = nextRows[i];
                        if (!a || !b) {
                            same = false;
                            break;
                        }
                        if (
                            a.latitude !== b.latitude ||
                            a.longitude !== b.longitude ||
                            a.speed !== b.speed ||
                            a.time !== b.time
                        ) {
                            same = false;
                            break;
                        }
                    }
                    if (same) return prev;
                }
                return nextRows;
            });
        };

        try {
            es = new EventSource(url);
            es.addEventListener('live', (event) => {
                consecutiveErrors = 0;
                try {
                    const payload = JSON.parse(event.data);
                    if (payload?.success && Array.isArray(payload?.data)) {
                        applyLiveData(payload.data);
                    }
                } catch (e) {
                    // ignore malformed frame
                }
            });
            es.onerror = () => {
                consecutiveErrors += 1;
                if (consecutiveErrors >= 5) {
                    if (es) es.close();
                    startFallbackPolling();
                }
            };
        } catch (e) {
            startFallbackPolling();
        }

        return () => {
            if (es) es.close();
            if (fallbackInterval) clearInterval(fallbackInterval);
        };
    }, [jwt?.token, hasDeviceFilter, normalizedDeviceFilter, filterToDevice]);

    useEffect(() => {
        if (!Array.isArray(liveLocationData)) return;
        if (typeof window === 'undefined') return;
        window.dispatchEvent(
            new CustomEvent('vms:live-data', {
                detail: {
                    data: liveLocationData,
                    updatedAt: Date.now(),
                },
            })
        );
    }, [liveLocationData]);

    useEffect(() => {
        if (!liveLocationData.length) {
            if (hasDeviceFilter) {
                setTrails({});
                setSnappedTrails({});
                segmentAnimRef.current = {};
            }
            return;
        }
        setTrails((prev) => {
            const liveKeys = new Set(liveLocationData.map((m, index) => vehicleKey(m, index)));
            const next = {};
            Object.keys(prev).forEach((k) => {
                if (liveKeys.has(k)) next[k] = prev[k];
            });
            liveLocationData.forEach((m, index) => {
                const k = vehicleKey(m, index);
                const coords = getMarkerCoords(m);
                if (!coords) return;
                const { lat, lng } = coords;
                const arr = next[k] ? [...next[k]] : [];
                const last = arr.length ? arr[arr.length - 1] : null;
                const movedEnough = !last || haversineKm(last.lat, last.lng, lat, lng) >= 0.012;
                if (movedEnough) {
                    const toPoint = { lat, lng };
                    const fromPoint = last ? { lat: last.lat, lng: last.lng } : toPoint;
                    segmentAnimRef.current[k] = {
                        from: fromPoint,
                        to: toPoint,
                        start: performance.now(),
                        duration: SEGMENT_ANIM_DURATION_MS,
                        heading: bearingDeg(fromPoint.lat, fromPoint.lng, toPoint.lat, toPoint.lng),
                    };
                    const sp = Number(m.speed);
                    arr.push({
                        lat,
                        lng,
                        speed: Number.isFinite(sp) ? sp : null,
                    });
                    while (arr.length > 1200) {
                        arr.shift();
                    }
                }
                next[k] = arr;
            });
            return next;
        });
        setSnappedTrails((prev) => {
            const liveKeys = new Set(liveLocationData.map((m, index) => vehicleKey(m, index)));
            const next = {};
            Object.keys(prev).forEach((k) => {
                if (liveKeys.has(k)) next[k] = prev[k];
            });
            return next;
        });
    }, [liveLocationData, hasDeviceFilter]);

    useEffect(() => {
        if (!selectedKey || !Array.isArray(liveLocationData) || !liveLocationData.length) return;
        const idx = liveLocationData.findIndex((m, i) => vehicleKey(m, i) === selectedKey);
        if (idx >= 0) {
            setSelectedMarker(liveLocationData[idx]);
        }
    }, [liveLocationData, selectedKey]);

    const trailLinesGeoJson = useMemo(() => {
        const features = [];
        Object.keys(trails).forEach((k) => {
            const pts = snappedTrails[k] || trails[k];
            if (!pts || pts.length < 2) return;
            const color = colorForKey(k);
            const lineStrings = sameDirectionLineStrings(pts);
            lineStrings.forEach((coordinates, runIdx) => {
                features.push({
                    type: 'Feature',
                    properties: { color, key: k, run: runIdx },
                    geometry: {
                        type: 'LineString',
                        coordinates,
                    },
                });
            });
        });
        return {
            type: 'FeatureCollection',
            features,
        };
    }, [trails, snappedTrails]);

    useEffect(() => {
        if (!trailLinesGeoJson.features.length) {
            return undefined;
        }
        let cancelled = false;
        let rafId;
        const tick = () => {
            if (cancelled) return;
            const map = mapRef.current && mapRef.current.getMap && mapRef.current.getMap();
            if (map && map.getLayer && map.getLayer(TRAIL_LINE_LAYER_ID)) {
                const t = performance.now();
                const pulse = 0.82 + Math.sin(t / 500) * 0.1;
                map.setPaintProperty(TRAIL_LINE_LAYER_ID, 'line-opacity', pulse);
            }
            rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);
        return () => {
            cancelled = true;
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [trailLinesGeoJson.features.length]);

    useEffect(() => {
        let cancelled = false;
        let rafId;
        let lastUpdate = 0;
        const tick = (now) => {
            if (cancelled) return;
            if (now - lastUpdate >= 90) {
                lastUpdate = now;
                setTrailAnimNow(now);
            }
            rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);
        return () => {
            cancelled = true;
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, []);

    const getLiveLocation = () => {
        var body = {
            tracker_token: localStorage.getItem('TrackerToken'),
        };
        if (hasDeviceFilter) {
            body.device_name = normalizedDeviceFilter;
        }
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + jwt.token },
            body: JSON.stringify(body),
        };
        var apiEnd = 'liveTracData';
        const apiBase =
            typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL
                ? process.env.REACT_APP_API_URL
                : '';
        fetch(apiBase + apiEnd, requestOptions)
            .then((resp) => {
                return resp.json();
            })
            .then((resp) => {
                if (resp.success === true) {
                    setLiveLocationData(filterToDevice(resp.data));
                } else {
                    console.log(resp.errorMessage);
                }
            })
            .catch((error) => {
                console.log(error, 'catch the hoop');
            });
    };

    const selectedSameDirectionKm = useMemo(() => {
        if (!selectedKey) return 0;
        return sameDirectionTrailKm(snappedTrails[selectedKey] || trails[selectedKey]);
    }, [selectedKey, trails, snappedTrails]);

    const perVehicleHeadingDeg = useMemo(() => {
        const out = {};
        Object.keys(trails).forEach((k) => {
            out[k] = headingForTrail(snappedTrails[k] || trails[k]);
        });
        return out;
    }, [trails, snappedTrails]);

    const animatedVehiclePositions = useMemo(() => {
        const out = {};
        const clamp01 = (x) => Math.min(1, Math.max(0, x));
        const easeInOutCubic = (t) => t * t * (3 - 2 * t);

        Object.keys(trails).forEach((k) => {
            const pts = snappedTrails[k] || trails[k];
            if (!pts || !pts.length) return;

            const last = pts[pts.length - 1];
            const anim = segmentAnimRef.current[k];

            if (!anim) {
                out[k] = {
                    lng: last.lng,
                    lat: last.lat,
                    heading: headingForTrail(pts),
                };
                return;
            }

            const t = clamp01((trailAnimNow - anim.start) / anim.duration);
            const tt = easeInOutCubic(t);
            const lng = anim.from.lng + (anim.to.lng - anim.from.lng) * tt;
            const lat = anim.from.lat + (anim.to.lat - anim.from.lat) * tt;
            out[k] = {
                lng,
                lat,
                heading: anim.heading,
            };
        });

        return out;
    }, [trails, snappedTrails, trailAnimNow]);

    const snapTraceToRoad = useCallback(
        async (k, shapePoints) => {
            const apiBase =
                typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL
                    ? process.env.REACT_APP_API_URL
                    : '';
            if (!apiBase) return;
            if (!shapePoints || shapePoints.length < 2) return;
            if (!jwt || !jwt.token) return;

            try {
                const resp = await fetch(apiBase + 'snapTrace', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + jwt.token,
                    },
                    body: JSON.stringify({
                        shape: shapePoints, // [{lat, lon}, ...]
                        costing: 'auto',
                    }),
                });
                const json = await resp.json();
                if (!json || json.success !== true) return;
                const points = json?.data?.points;
                if (!Array.isArray(points) || points.length < 2) return;

                const normalized = points
                    .map((p) => {
                        const lat = Number(p.lat);
                        const lng = Number(p.lon ?? p.lng);
                        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
                        return { lat, lng };
                    })
                    .filter(Boolean);
                if (normalized.length < 2) return;

                setSnappedTrails((prev) => {
                    const existing = prev[k] ? [...prev[k]] : [];
                    const lastExisting = existing.length ? existing[existing.length - 1] : null;
                    const firstNew = normalized[0];

                    // If first snapped point overlaps the previous snapped end, drop it to keep line continuous.
                    const toAppend = [];
                    if (lastExisting && firstNew) {
                        const dKm = haversineKm(lastExisting.lat, lastExisting.lng, firstNew.lat, firstNew.lng);
                        if (dKm < 0.002) {
                            toAppend.push(...normalized.slice(1));
                        } else {
                            toAppend.push(...normalized);
                        }
                    } else {
                        toAppend.push(...normalized);
                    }

                    const merged = existing.length ? existing.concat(toAppend) : toAppend;
                    while (merged.length > 1200) merged.shift();
                    return { ...prev, [k]: merged };
                });

                // Retarget the moving segment endpoints so the car stays on the snapped line.
                const anim = segmentAnimRef.current[k];
                if (anim && normalized.length >= 2) {
                    const fromP = normalized[0];
                    const toP = normalized[normalized.length - 1];
                    anim.from = { lat: fromP.lat, lng: fromP.lng };
                    anim.to = { lat: toP.lat, lng: toP.lng };
                    anim.heading = bearingDeg(fromP.lat, fromP.lng, toP.lat, toP.lng);
                }
            } catch (e) {
                // Ignore snap failures; fall back to raw GPS trail.
            }
        },
        [jwt]
    );

    useEffect(() => {
        // Snap only when a new raw point is added (cheap window: last 3 points).
        if (!trails || !Object.keys(trails).length) return;
        Object.keys(trails).forEach((k) => {
            const pts = trails[k];
            if (!pts || pts.length < 2) return;
            const len = pts.length;
            const prevLen = lastTrailsLenRef.current[k] || 0;
            if (len <= prevLen) return;
            lastTrailsLenRef.current[k] = len;

            // Snap only the latest segment endpoints (last 2 raw points).
            // This keeps the animated marker segment aligned with the snapped geometry.
            const slice = pts.slice(Math.max(0, len - 2));
            const shapePoints = slice.map((p) => ({ lat: p.lat, lon: p.lng }));
            snapTraceToRoad(k, shapePoints);
        });
    }, [trails, snapTraceToRoad]);

    

    const openMarker = useCallback((marker, index) => {
        setSelectedKey(vehicleKey(marker, index));
        setSelectedMarker(marker);
        setLocationName('…');
        const coords = getMarkerCoords(marker);
        if (!coords) {
            setLocationName('');
            return;
        }
        enqueueGeocode(coords.lat, coords.lng).then(setLocationName);
    }, []);

    const handleInfoWindowClose = () => {
        setSelectedKey(null);
        setSelectedMarker(null);
        setLocationName('');
    };

    const showIndividualMarkers = hasDeviceFilter || mapZoom >= MIN_ZOOM_VEHICLE_MARKERS;

    useEffect(() => {
        if (!showIndividualMarkers && selectedMarker) {
            setSelectedKey(null);
            setSelectedMarker(null);
            setLocationName('');
        }
    }, [showIndividualMarkers, selectedMarker]);

    useEffect(() => {
        if (!hasDeviceFilter || !Array.isArray(liveLocationData) || !liveLocationData.length) return;
        const first = liveLocationData[0];
        const coords = getMarkerCoords(first);
        if (!coords) return;
        const map = mapRef.current?.getMap?.();
        if (!map) return;
        map.easeTo({
            center: [coords.lng, coords.lat],
            zoom: Math.max(Number(mapZoom) || 0, 14),
            duration: 450,
        });
    }, [hasDeviceFilter, liveLocationData, mapZoom]);

    const onMapMove = useCallback((evt) => {
        setMapZoom(evt.viewState.zoom);
    }, []);

    const filteredLiveData = liveLocationData;

    const vehiclePoints = useMemo(() => {
        return (filteredLiveData || [])
            .map((m, idx) => {
                const coords = getMarkerCoords(m);
                if (!coords) return null;
                const key = vehicleKey(m, idx);
                const snapped = snappedTrails[key];
                const snappedTip =
                    Array.isArray(snapped) && snapped.length
                        ? snapped[snapped.length - 1]
                        : null;
                return {
                    marker: m,
                    index: idx,
                    key,
                    lat: snappedTip?.lat ?? coords.lat,
                    lng: snappedTip?.lng ?? coords.lng,
                };
            })
            .filter(Boolean);
    }, [filteredLiveData, snappedTrails]);

    const groupedCountMarkers = useMemo(() => {
        // Fallback grouping marker renderer (independent from style glyphs / cluster layers).
        // Use smaller cells even at low zoom so counts stay lower per bubble.
        const z = Number(mapZoom) || 0;
        if (z < 6) {
            if (!vehiclePoints.length) return [];
            let sumLat = 0;
            let sumLng = 0;
            vehiclePoints.forEach((p) => {
                sumLat += p.lat;
                sumLng += p.lng;
            });
            return [{
                lat: sumLat / vehiclePoints.length,
                lng: sumLng / vehiclePoints.length,
                count: vehiclePoints.length,
                isTotal: true,
            }];
        }
        const cellSizeDeg = z < 8 ? 0.2 : z < 10 ? 0.12 : 0.07;
        const groups = new window.Map();

        vehiclePoints.forEach((p) => {
            const gx = Math.round(p.lng / cellSizeDeg) * cellSizeDeg;
            const gy = Math.round(p.lat / cellSizeDeg) * cellSizeDeg;
            const key = `${gx.toFixed(4)}|${gy.toFixed(4)}`;
            const prev = groups.get(key);
            if (prev) {
                prev.count += 1;
            } else {
                groups.set(key, { lng: gx, lat: gy, count: 1 });
            }
        });

        return Array.from(groups.values());
    }, [vehiclePoints, mapZoom]);
    const selectedCoords = useMemo(() => getMarkerCoords(selectedMarker), [selectedMarker]);

    const onGroupedMarkerClick = useCallback((g) => {
        const map = mapRef.current?.getMap?.();
        if (!map || !g) return;
        map.easeTo({
            center: [g.lng, g.lat],
            zoom: Math.max((Number(mapZoom) || 0) + 2, MIN_ZOOM_VEHICLE_MARKERS),
            duration: 500,
        });
    }, [mapZoom]);

    useEffect(() => {
        const onExternalFocus = (evt) => {
            const detail = evt?.detail || {};
            const vehicleName = String(detail.vehicleName || '').trim();
            const openModal = !!detail.openModal;
            if (!vehicleName || !Array.isArray(liveLocationData) || !liveLocationData.length) return;

            const matchIndex = liveLocationData.findIndex((m, i) => {
                const name = String(m?.name || m?.device_name || '').trim();
                if (name && name === vehicleName) return true;
                return vehicleKey(m, i) === vehicleName;
            });
            if (matchIndex < 0) return;

            const match = liveLocationData[matchIndex];
            const coords = getMarkerCoords(match);
            if (!coords) return;

            const map = mapRef.current?.getMap?.();
            if (map) {
                map.easeTo({
                    center: [coords.lng, coords.lat],
                    zoom: Math.max(Number(mapZoom) || 0, MIN_ZOOM_VEHICLE_MARKERS),
                    duration: 550,
                });
            }

            if (openModal) {
                openMarker(match, matchIndex);
            } else {
                setSelectedKey(vehicleKey(match, matchIndex));
                setSelectedMarker(null);
                setLocationName('');
            }
        };

        window.addEventListener('vms:focus-vehicle', onExternalFocus);
        return () => window.removeEventListener('vms:focus-vehicle', onExternalFocus);
    }, [liveLocationData, mapZoom, openMarker]);

    return (
        <div style={{ height: '900px', width: '100%', position: 'relative' }}>
            <style>
                {`
                    @keyframes vehicleBob {
                        0%, 100% { transform: translateY(0); }
                        50% { transform: translateY(-2px); }
                    }
                `}
            </style>
            <Map
                ref={mapRef}
                mapLib={maplibreLib}
                mapStyle={mapStyle}
                initialViewState={initialViewState}
                style={mapDivStyle}
                onMove={onMapMove}
            >
                <NavigationControl position="top-right" />
                {trailLinesGeoJson.features.length > 0 && (
                    <Source id="live-trails" type="geojson" data={trailLinesGeoJson}>
                        <Layer
                            id={TRAIL_LINE_LAYER_ID}
                            type="line"
                            layout={{
                                'line-join': 'round',
                                'line-cap': 'round',
                            }}
                            paint={{
                                'line-color': '#16a34a',
                                'line-width': 5,
                                'line-opacity': 0.95,
                            }}
                        />
                    </Source>
                )}

                {!showIndividualMarkers &&
                    groupedCountMarkers.map((g, idx) => {
                        const size = g.isTotal ? 52 : g.count >= 50 ? 46 : g.count >= 10 ? 40 : 34;
                        const bg =
                            g.isTotal
                                ? 'radial-gradient(circle at 30% 30%, #22d3ee 0%, #2563eb 75%)'
                                : g.count >= 50
                                ? 'radial-gradient(circle at 30% 30%, #f87171 0%, #dc2626 72%)'
                                : g.count >= 10
                                ? 'radial-gradient(circle at 30% 30%, #fde68a 0%, #f59e0b 72%)'
                                : 'radial-gradient(circle at 30% 30%, #60a5fa 0%, #2563eb 72%)';
                        const textColor = g.isTotal ? '#ffffff' : (g.count >= 10 && g.count < 50 ? '#1f2937' : '#ffffff');
                        return (
                            <Marker
                                key={`group-${idx}-${g.lat}-${g.lng}`}
                                longitude={g.lng}
                                latitude={g.lat}
                                anchor="center"
                                onClick={(e) => {
                                    e.originalEvent.stopPropagation();
                                    onGroupedMarkerClick(g);
                                }}
                            >
                                <div
                                    style={{
                                        width: size,
                                        height: size,
                                        borderRadius: '50%',
                                        background: bg,
                                        color: textColor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: g.count >= 100 ? 12 : 13,
                                        fontWeight: 800,
                                        border: '2px solid rgba(255,255,255,0.95)',
                                        boxShadow: '0 3px 10px rgba(15,23,42,0.32)',
                                        lineHeight: 1,
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    {g.count}
                                </div>
                            </Marker>
                        );
                    })}

                {showIndividualMarkers &&
                    filteredLiveData.map((marker, index) => {
                        const coords = getMarkerCoords(marker);
                        if (!coords) return null;
                        const key = vehicleKey(marker, index);
                        const snapped = snappedTrails[key];
                        const snappedTip =
                            Array.isArray(snapped) && snapped.length
                                ? snapped[snapped.length - 1]
                                : null;
                        const animatedPos = animatedVehiclePositions[key];
                        const markerLng = animatedPos
                            ? animatedPos.lng
                            : (snappedTip?.lng ?? coords.lng);
                        const markerLat = animatedPos
                            ? animatedPos.lat
                            : (snappedTip?.lat ?? coords.lat);
                        return (
                            <Marker
                                key={`${marker.name || 'v'}-${index}-${coords.lat}-${coords.lng}`}
                                longitude={markerLng}
                                latitude={markerLat}
                                anchor="center"
                                onClick={(e) => {
                                    e.originalEvent.stopPropagation();
                                    openMarker(marker, index);
                                }}
                            >
                                <div style={{ position: 'relative', cursor: 'pointer' }}>
                                    {(() => {
                                        const speedNum = Number(marker.speed) || 0;
                                        const moving = speedNum > 0;
                                        const heading = animatedPos?.heading ?? perVehicleHeadingDeg[key] ?? 0;
                                        const rotationDeg = heading + CAR_ICON_HEADING_OFFSET_DEG;
                                        return (
                                            <UberLikeCarMarker rotationDeg={rotationDeg} moving={moving} />
                                        );
                                    })()}
                                </div>
                            </Marker>
                        );
                    })}
            </Map>
            {showIndividualMarkers && selectedMarker && (
                <div
                    onClick={handleInfoWindowClose}
                    style={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 5,
                        background: 'rgba(15,23,42,0.35)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 16,
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: 340,
                            maxWidth: '100%',
                            background: '#fff',
                            borderRadius: 12,
                            boxShadow: '0 12px 30px rgba(0,0,0,0.28)',
                            overflow: 'hidden',
                        }}
                    >
                        <div
                            style={{
                                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                                color: '#fff',
                                padding: '12px 14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <div style={{ fontWeight: 700, fontSize: 15 }}>
                                {selectedMarker?.name || selectedMarker?.device_name || 'Vehicle'}
                            </div>
                            <button
                                type="button"
                                onClick={handleInfoWindowClose}
                                style={{
                                    border: 'none',
                                    background: 'rgba(255,255,255,0.2)',
                                    color: '#fff',
                                    width: 28,
                                    height: 28,
                                    borderRadius: 8,
                                    cursor: 'pointer',
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <div style={{ padding: 14 }}>
                            <div className="small text-muted" style={{ marginBottom: 8 }}>
                                Live vehicle information
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 10px' }}>
                                    <div className="small text-muted">Speed</div>
                                    <div style={{ fontWeight: 700 }}>{Number(selectedMarker?.speed || 0)} km/h</div>
                                </div>
                                <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 10px' }}>
                                    <div className="small text-muted">Distance</div>
                                    <div style={{ fontWeight: 700 }}>
                                        {selectedSameDirectionKm.toFixed(2)} km
                                    </div>
                                </div>
                            </div>
                            <div style={{ marginTop: 10, background: '#f8fafc', borderRadius: 8, padding: '8px 10px' }}>
                                <div className="small text-muted">Location</div>
                                <div style={{ fontWeight: 600 }}>
                                    {locationName || (selectedCoords ? `${selectedCoords.lat.toFixed(5)}, ${selectedCoords.lng.toFixed(5)}` : '—')}
                                </div>
                            </div>
                            <div style={{ marginTop: 10, background: '#f8fafc', borderRadius: 8, padding: '8px 10px' }}>
                                <div className="small text-muted">Last Update</div>
                                <div style={{ fontWeight: 600 }}>{selectedMarker?.time || '—'}</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div
                className='small text-muted px-2 py-1 bg-light border-top'
                style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}
            >
                Road line shows travelled path distance. Vehicle icon animates through this line. Zoom past {MIN_ZOOM_VEHICLE_MARKERS} to show vehicle markers.
            </div>
        </div>
    );
}
export default VehicleLiveLocation;
